import { nextKitchenStatus, type Order } from '@momonos/shared'
import { Badge, cn } from '@momonos/ui'
import { AlertTriangle, ChevronRight, Clock, QrCode, ShoppingBag, Utensils } from 'lucide-react'
import { isOverdue, remainingSeconds } from '../lib/board'

/** FR-KDS-2 — everything the cook needs, readable at arm's length. */
export interface OrderCardProps {
  order: Order
  now: number
  isUnseen: boolean
  isBusy?: boolean
  onAdvance: (order: Order) => void
}

const CHANNEL_LABEL: Record<Order['channel'], { label: string; Icon: typeof QrCode }> = {
  kiosk: { label: 'Kiosk', Icon: Utensils },
  qr: { label: 'QR', Icon: QrCode },
  pos: { label: 'Counter', Icon: ShoppingBag },
  web: { label: 'Web', Icon: QrCode },
}

function formatCountdown(seconds: number): string {
  const overdue = seconds < 0
  const absolute = Math.abs(seconds)
  const minutes = Math.floor(absolute / 60)
  const remainder = absolute % 60
  return `${overdue ? '+' : ''}${minutes}:${String(remainder).padStart(2, '0')}`
}

export function OrderCard({ order, now, isUnseen, isBusy, onAdvance }: OrderCardProps) {
  const remaining = remainingSeconds(order, now)
  const overdue = isOverdue(order, now)
  const next = nextKitchenStatus(order.status)
  const channel = CHANNEL_LABEL[order.channel]

  return (
    <article
      data-testid={`order-${order.orderNumber}`}
      className={cn(
        'rounded-xl border-2 bg-surface-raised p-4 transition-colors',
        overdue
          ? 'animate-pulse-red border-kds-late'
          : order.isPriority
            ? 'border-momo-yellow'
            : 'border-surface-border',
        isUnseen && 'animate-flash',
      )}
    >
      <header className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p className="font-bold text-3xl text-content tabular">{order.orderNumber}</p>
          <p className="mt-1 flex items-center gap-1.5 text-content-muted text-sm">
            <channel.Icon aria-hidden="true" className="size-4" />
            {channel.label}
            {order.tableLabel && ` · Table ${order.tableLabel}`}
            {order.type === 'takeaway' && ' · Takeaway'}
          </p>
        </div>

        <div className="text-right">
          {/*
            role="timer" is the semantic match for a countdown and, unlike a
            bare <p>, it supports the label. aria-live is off deliberately: a
            live region would announce every single second.
          */}
          <p
            role="timer"
            aria-live="off"
            className={cn(
              'flex items-center gap-1.5 font-bold text-2xl tabular',
              overdue ? 'text-kds-late' : remaining < 60 ? 'text-momo-yellow' : 'text-content',
            )}
            // Screen readers should hear "late", not a bare number.
            aria-label={
              overdue ? `${formatCountdown(remaining)} overdue` : formatCountdown(remaining)
            }
          >
            {overdue ? (
              <AlertTriangle aria-hidden="true" className="size-5" />
            ) : (
              <Clock aria-hidden="true" className="size-5" />
            )}
            {formatCountdown(remaining)}
          </p>
          {order.isPriority && (
            <Badge tone="yellow" className="mt-1">
              Priority
            </Badge>
          )}
        </div>
      </header>

      <ul className="space-y-2 border-surface-border border-t pt-3">
        {order.items.map((item) => (
          <li key={item.id}>
            <div className="flex items-baseline gap-2">
              <span className="min-w-8 font-bold text-content text-xl tabular">
                {item.quantity}×
              </span>
              <span className="font-medium text-content text-lg">
                {item.productName.en}
                {item.variantName && (
                  <span className="text-content-secondary"> · {item.variantName.en}</span>
                )}
              </span>
            </div>

            {item.modifiers.length > 0 && (
              <p className="ml-10 text-momo-yellow text-sm">
                {item.modifiers.map((modifier) => modifier.name.en).join(', ')}
              </p>
            )}

            {item.note && (
              <p className="ml-10 font-medium text-momo-red text-sm">Note: {item.note}</p>
            )}
          </li>
        ))}
      </ul>

      {order.note && (
        <p className="mt-3 rounded-lg bg-momo-red/15 px-3 py-2 font-medium text-momo-red text-sm">
          {order.note}
        </p>
      )}

      {next && (
        <button
          type="button"
          disabled={isBusy}
          onClick={() => onAdvance(order)}
          // Big tap target: greasy hands, cheap tablets, no mouse (FR-KDS-1).
          className={cn(
            'mt-4 flex h-touch w-full items-center justify-center gap-2 rounded-lg font-semibold text-lg',
            'bg-surface-overlay text-content transition-colors hover:bg-momo-red active:scale-[0.99]',
            'disabled:opacity-50 touch-surface',
          )}
        >
          {ADVANCE_LABEL[next] ?? 'Next'}
          <ChevronRight aria-hidden="true" className="size-5" />
        </button>
      )}
    </article>
  )
}

const ADVANCE_LABEL: Record<string, string> = {
  accepted: 'Accept',
  preparing: 'Start cooking',
  ready: 'Mark ready',
  delivered: 'Handed over',
}
